import { Getter, Mutation, Store } from 'vuex';
import { GlobalSearchState, RootState, Command, GlobalSearchProjectState } from '@/store/types';
import { USER_SET_DATA } from '@/store/mutation-types';
import { ascend, prop, sort, sortWith } from 'ramda';

type InitializePayload = { userId: string; projectId: string };

const getLocalStorageKey = (userId: string) => `atlasticloud${userId}-globalsearchconfig`;

/**
 * Sort alphabetically by group to keep grouped entries in order, then alphabetically inside the group
 */
const sortCommands = sortWith<Command & { id: string }>([ascend(prop('group')), ascend(prop('translationKey'))]);

const getStateFromStorage = (state: GlobalSearchState, userId: string): GlobalSearchState => {
  const item = localStorage.getItem(getLocalStorageKey(userId));

  if (item) {
    const parsed = JSON.parse(item);

    if (!parsed.projects) {
      // projects was added later and may not exist
      parsed.projects = {};
    }

    return parsed;
  }

  return Object.assign({}, state, { userId });
};

const storeConfig = (state: GlobalSearchState) => {
  const stateToStore = {
    ...state,
  };

  delete stateToStore.commands;
  delete stateToStore.latestGroups;

  localStorage.setItem(getLocalStorageKey(state.userId), JSON.stringify(stateToStore));
};

const updateProjectAndStoreConfig = (
  state: GlobalSearchState,
  projectId: string,
  newConfig: Partial<GlobalSearchProjectState>
) => {
  if (!state.projects[projectId]) {
    state.projects[projectId] = {
      searchTerms: [],
      sourceIds: [],
      sourceGroupIds: [],
    };
  }

  Object.entries(newConfig).forEach(([key, value]) => {
    state.projects[projectId][key] = value;
  });

  storeConfig(state);
};

const getters: Record<string, Getter<GlobalSearchState, RootState>> = {
  projectState(state) {
    return (projectId: string) => state.projects[projectId] ?? {};
  },
};

const mutations: Record<string, Mutation<GlobalSearchState>> = {
  initialize(state: GlobalSearchState, { userId }: InitializePayload) {
    const storedState = getStateFromStorage(state, userId);

    state.projects = storedState.projects;
    state.userId = storedState.userId;
    storeConfig(state);
  },
  setSearchTerms(
    state: GlobalSearchState,
    { projectId, searchTerms }: { projectId: string; searchTerms: Array<string> }
  ) {
    updateProjectAndStoreConfig(state, projectId, { searchTerms });
  },
  setSourceIds(state: GlobalSearchState, { projectId, sourceIds }: { projectId: string; sourceIds: Array<string> }) {
    updateProjectAndStoreConfig(state, projectId, { sourceIds });
  },
  setSourceGroupIds(
    state: GlobalSearchState,
    { projectId, sourceGroupIds }: { projectId: string; sourceGroupIds: Array<string> }
  ) {
    updateProjectAndStoreConfig(state, projectId, { sourceGroupIds });
  },
  setModalSearch(state, search: string) {
    state.search = search;
  },
  setModalValue(state, value: string) {
    state.value = value;
  },
  addCommands(state, commandSet: Array<Command & { id: string }>) {
    state.latestGroups = new Set([...commandSet.map((command) => command.group), ...state.latestGroups]);

    const latestGroups = [...state.latestGroups];

    let commands = sortCommands([...commandSet, ...state.commands]);

    // Move the latest added or used groups to the top
    commands = sort((cmdA, cmdB) => latestGroups.indexOf(cmdA.group) - latestGroups.indexOf(cmdB.group), commands);

    state.commands = commands;
  },
  removeCommands(state, commandSet: Array<Command & { id: string }>) {
    const commandIds: Array<string> = [];

    commandSet.forEach((command) => {
      commandIds.push(command.id);
    });

    state.commands = state.commands.filter((command) => !commandIds.includes(command.id));
  },
};

// Plugin to initialize globalSearch state when user ID is set
const globalSearchInitPlugin = (store: Store<RootState>) => {
  store.subscribe((mutation) => {
    if (mutation.type === `user/${USER_SET_DATA}`) {
      store.commit('globalSearch/initialize', { userId: mutation.payload.id });
    }
  });
};

export default {
  namespaced: true,
  state: {
    userId: '',
    projects: {},
    search: '',
    value: '',
    commands: [],
    latestGroups: new Set(),
  } satisfies GlobalSearchState,
  mutations,
  plugins: [globalSearchInitPlugin],
  getters,
};
